import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Send, AlarmClock } from 'lucide-react';

const LABELS = { unlock_reminders: 'Unlock reminders', expiry_alerts: 'Expiry alerts', due_reminders: 'Due-date reminders', content_backup: 'Course backups saved' };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

export const DailyEmailsCard = () => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get('/cron/daily-emails/status').then((r) => setData(r.data)).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setBusy(true);
    try {
      await api.post('/cron/daily-emails/run');
      toast.success('Sending now — results appear below in a few moments');
      setTimeout(load, 4000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start the run');
    } finally { setBusy(false); }
  };

  const last = data?.runs?.[0];
  return (
    <Card className="border-slate-200 mt-4" data-testid="daily-emails-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-lg flex items-center gap-2"><AlarmClock className="h-5 w-5 text-[#20acf6]" /> Daily emails</CardTitle>
        <Button size="sm" onClick={runNow} disabled={busy} className="bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="daily-emails-send-now">
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Send now
        </Button>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-slate-600">Unlock reminders, training expiry alerts and due-date reminders go out automatically every day at 8:00am UK time. If the server was asleep at 8am, they go out as soon as it wakes. Nobody is emailed twice for the same thing.</p>
        {data && (
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-slate-600">
            <span>Next run: <strong data-testid="daily-emails-next-run">{fmt(data.next_run_at)}</strong></span>
            <span>Sending via: <strong data-testid="daily-emails-provider">{data.email_provider === 'sendgrid' ? 'SendGrid' : data.email_provider === 'console' ? 'Console (test mode, not delivered)' : data.email_provider}</strong></span>
          </div>
        )}
        {last ? (
          <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="daily-emails-last-run">
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-800">Last run {fmt(last.started_at)}</span>
              <Badge className="bg-slate-100 text-slate-600">{last.trigger === 'manual' ? 'Send now' : 'Scheduled'}</Badge>
              <Badge className={last.status === 'done' ? 'bg-green-100 text-green-700' : last.status === 'partial' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'} data-testid="daily-emails-last-status">
                {last.status === 'done' ? 'Completed' : last.status === 'partial' ? 'Some jobs failed' : 'Running'}
              </Badge>
            </div>
            {last.results && (
              <div className="flex flex-wrap gap-4 text-slate-600">
                {Object.entries(last.results).map(([k, r]) => (
                  <span key={k} data-testid={`daily-emails-result-${k}`}>{LABELS[k] || k}: <strong>{r.ok ? `${r.sent} ${k === 'content_backup' ? 'saved' : 'sent'}` : 'failed'}</strong></span>
                ))}
              </div>
            )}
          </div>
        ) : data && <p className="text-slate-400" data-testid="daily-emails-no-runs">No runs yet.</p>}
      </CardContent>
    </Card>
  );
};
